import React, { useEffect, useState } from "react";
import { Formik, Field, Form, FieldArray } from "formik";
import * as Yup from "yup";
import {
    Box,
    Button,
    Input,
    Select,
    VStack,
    HStack,
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    TableContainer,
    Heading,
    IconButton,
    FormLabel,
} from "@chakra-ui/react";
import { LuTrash2 } from "react-icons/lu";
import { fetchWrapper } from "../../helpers";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

// Validation Schema
const validationSchema = Yup.object().shape({
    currency: Yup.string().required("Currency is required"),
    pairs: Yup.array().of(
        Yup.object().shape({
            from: Yup.string().required("From field is required"),
            till: Yup.string().required("Till field is required"),
            price: Yup.string().required("Price field is required"),
        })
    ),
});

const CurrencyForm = ({ data }) => {
    const [currencyList, setCurrencyList] = useState([]);
    const [loading, setLoading] = useState(false);
    const { id } = useParams();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [tempSlab, setTempSlab] = useState({ from: "", till: "", price: "" });
    const [currentPush, setCurrentPush] = useState(null);

    const [initialValues, setInitialValues] = useState({
        currency: "",
        pairs: [],
    });

    useEffect(() => {
        loadCurrency();
    }, []);

    useEffect(() => {
        if (data?.mau) {
            setInitialValues({
                currency: data.mau.currency || "",
                pairs: data.mau.pairs?.length ? data.mau.pairs : [],
            });
        }
    }, [data]);

    const loadCurrency = async () => {
        const url = `${process.env.REACT_APP_API_URL}/currency/list`;
        const currencyData = await fetchWrapper.get(url);
        setCurrencyList(currencyData?.data);
    };

    const onSubmit = async (values) => {
        let payload = { id, mau: { ...values } };
        try {
            setLoading(true);
            const url = `${process.env.REACT_APP_API_URL}/sale/add-mau`;
            await fetchWrapper.post(url, payload);
            toast("Record has been saved", { type: "info" });
            setLoading(false);
        } catch (error) {
            toast("Record has not been saved", { type: "error" });
            setLoading(false);
        }
    };

    const handleAddSlab = () => {
        if (tempSlab.from && tempSlab.till && tempSlab.price && currentPush) {
            currentPush({ from: tempSlab.from, till: tempSlab.till, price: tempSlab.price });
            setTempSlab({ from: "", till: "", price: "" });
            setIsModalOpen(false);
        }
    };

    const formatNumber = (num) => {
        if (!num) return "";
        return parseFloat(num).toLocaleString();
    };

    return (
        <Formik
            enableReinitialize={true}
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={onSubmit}
        >
            {({ values, errors, touched }) => (
                <Form>
                    <VStack spacing={6} align="stretch">
                        {/* Currency Select */}
                        <Box bg="white" p={6} borderRadius="lg" shadow="sm" border="1px" borderColor="gray.200" display={"flex"} justifyContent={"space-between"} alignItems={"center"}>
                            <Box>
                                <FormLabel fontWeight="semibold">Currency</FormLabel>
                                <Field name="currency">
                                    {({ field }) => (
                                        <Select {...field} placeholder="Select currency" width={{ base: "100%", md: "300px" }}>
                                            {currencyList?.map((currency) => (
                                                <option key={currency.id} value={currency.id}>
                                                    {currency.name}
                                                </option>
                                            ))}
                                        </Select>
                                    )}
                                </Field>
                                {errors.currency && touched.currency && (
                                    <Text color="red.500" fontSize="sm" mt={1}>
                                        {errors.currency}
                                    </Text>
                                )}
                            </Box>
                            <Button
                                type="submit"
                                bg="#2563eb" color="white" hover={"#2563eb"} size={"sm"}
                                isLoading={loading}
                                loadingText="Saving..."
                                px={8}
                            >
                                Update
                            </Button>
                        </Box>

                        {/* MAU Slabs Table */}
                        <Box bg="white" borderRadius="lg" shadow="sm" border="1px" borderColor="gray.200">
                            <Box p={6} borderColor="gray.200">
                                <HStack justify="space-between" align="center" flexWrap="wrap" gap={4}>
                                    <Heading size="md" fontWeight="semibold">
                                        MAU (Monthly Active Users) Slabs
                                    </Heading>
                                    <FieldArray name="pairs">
                                        {({ push }) => {
                                            if (!currentPush) {
                                                setCurrentPush(() => push);
                                            }
                                            return (
                                                <Button
                                                    // leftIcon={<LuPlus />}
                                                    bg="#2563eb" color="white" hover={"#2563eb"} size={"sm"}
                                                    onClick={() => setIsModalOpen(true)}
                                                    isDisabled={!values.currency}
                                                >
                                                    Add MAU Slab
                                                </Button>
                                            );
                                        }}
                                    </FieldArray>
                                </HStack>
                            </Box>

                            <Box p={6}>
                                <FieldArray name="pairs">
                                    {({ remove }) => (
                                        <TableContainer width="100%"
                                            bg="white"
                                            borderRadius="lg"
                                            border="1px solid"
                                            borderColor="gray.200"
                                            overflow="hidden">
                                            <Table
                                                variant="simple" width="100%"
                                                mb={10}
                                            >
                                                <Thead bg="#f7f7f7">
                                                    <Tr>
                                                        <Th minW="120px">From Range</Th>
                                                        <Th minW="120px">To Range</Th>
                                                        <Th minW="140px">Cost per MAU</Th>
                                                        <Th textAlign="right" minW="100px">Actions</Th>
                                                    </Tr>
                                                </Thead>
                                                <Tbody>
                                                    {values.pairs.length === 0 ? (
                                                        <Tr>
                                                            <Td colSpan={4} textAlign="center" py={8}>
                                                                <Text color="gray.500">
                                                                    No MAU slabs configured. Add your first slab to get started.
                                                                </Text>
                                                            </Td>
                                                        </Tr>
                                                    ) : (
                                                        values.pairs.map((pair, index) => (
                                                            <Tr key={index}>
                                                                <Td>{formatNumber(pair.from)}</Td>
                                                                <Td>
                                                                    {pair.till.toLowerCase() === "unlimited"
                                                                        ? "Unlimited"
                                                                        : formatNumber(pair.till)}
                                                                </Td>
                                                                <Td>{parseFloat(pair.price).toFixed(2)}</Td>
                                                                <Td textAlign="right">
                                                                    <IconButton
                                                                        icon={<LuTrash2 />}
                                                                        size="sm"
                                                                        colorScheme="red"
                                                                        variant="ghost"
                                                                        onClick={() => remove(index)}
                                                                        aria-label="Delete slab"
                                                                    />
                                                                </Td>
                                                            </Tr>
                                                        ))
                                                    )}
                                                </Tbody>
                                            </Table>
                                        </TableContainer>
                                    )}
                                </FieldArray>
                            </Box>
                        </Box>


                    </VStack>

                    {/* Add MAU Slab Modal */}
                    {isModalOpen && (
                        <Box
                            position="fixed"
                            top="0"
                            left="0"
                            right="0"
                            bottom="0"
                            bg="blackAlpha.600"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            zIndex={1400}
                            onClick={() => setIsModalOpen(false)}
                        >
                            <Box
                                bg="white"
                                borderRadius="lg"
                                p={6}
                                maxW="500px"
                                w="90%"
                                shadow="xl"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Heading size="md" mb={4}>Add MAU Slab</Heading>
                                <Text fontSize="sm" color="gray.600" mb={4}>
                                    Configure pricing based on Monthly Active Users ranges.
                                </Text>
                                <VStack spacing={4}>
                                    <Box w="full">
                                        <FormLabel>From Range</FormLabel>
                                        <Input
                                            type="number"
                                            placeholder="e.g., 1000"
                                            value={tempSlab.from}
                                            onChange={(e) => setTempSlab({ ...tempSlab, from: e.target.value })}
                                        />
                                    </Box>

                                    <Box w="full">
                                        <FormLabel>To Range</FormLabel>
                                        <Input
                                            placeholder="e.g., 5000 or 'unlimited'"
                                            value={tempSlab.till}
                                            onChange={(e) => setTempSlab({ ...tempSlab, till: e.target.value })}
                                        />
                                    </Box>

                                    <Box w="full">
                                        <FormLabel>Price (Per MAU)</FormLabel>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            placeholder="e.g., 0.05"
                                            value={tempSlab.price}
                                            onChange={(e) => setTempSlab({ ...tempSlab, price: e.target.value })}
                                        />
                                    </Box>

                                    <HStack spacing={3} w="full" justify="flex-end" pt={4}>
                                        <Button size="sm"
                                            border={"1px solid #0000001a"}
                                            color={"#0a0a0a"}
                                            bg={"white"}
                                            _hover={{
                                                bg: "#e2e8f0 !important",
                                                color: "#0a0a0a",
                                            }} onClick={() => setIsModalOpen(false)}>
                                            Cancel
                                        </Button>
                                        <Button bg="#2563eb" color="white" hover={"#2563eb"} size={"sm"} onClick={handleAddSlab}>
                                            Add Slab
                                        </Button>
                                    </HStack>
                                </VStack>
                            </Box>
                        </Box>
                    )}
                </Form>
            )}
        </Formik>
    );
};

export default CurrencyForm;